import { useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, MapPin, Plus, Pencil, Trash2, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { PageHeader } from "./profiel";

export const Route = createFileRoute("/_authenticated/account/adressen")({
  head: () => ({
    meta: [
      { title: "Adressen — Elvion Group Nederland" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AddressesPage,
});

type Address = {
  id: string;
  label: string | null;
  street: string;
  house_number: string;
  postal_code: string;
  city: string;
  country: string;
  is_billing: boolean;
  is_shipping: boolean;
};

type FormState = {
  id?: string;
  label: string;
  street: string;
  house_number: string;
  postal_code: string;
  city: string;
  country: string;
  is_billing: boolean;
  is_shipping: boolean;
};

const empty: FormState = {
  label: "",
  street: "",
  house_number: "",
  postal_code: "",
  city: "",
  country: "Nederland",
  is_billing: true,
  is_shipping: true,
};

function AddressesPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [lookupState, setLookupState] = useState<"idle" | "loading" | "found" | "notfound">("idle");
  const lookupAbort = useRef<AbortController | null>(null);

  // Autofill straat + plaats via PDOK Locatieserver zodra postcode + huisnummer geldig zijn
  useEffect(() => {
    if (!editing) return;
    const pc = editing.postal_code.replace(/\s+/g, "").toUpperCase();
    const hn = editing.house_number.trim();
    if (!/^[1-9][0-9]{3}[A-Z]{2}$/.test(pc) || !/^\d+/.test(hn)) {
      setLookupState("idle");
      return;
    }
    lookupAbort.current?.abort();
    const ctrl = new AbortController();
    lookupAbort.current = ctrl;
    const timer = setTimeout(async () => {
      setLookupState("loading");
      try {
        const houseNum = hn.match(/^\d+/)?.[0] ?? hn;
        const q = encodeURIComponent(`postcode:${pc} and huisnummer:${houseNum}`);
        const url = `https://api.pdok.nl/bzk/locatieserver/search/v3_1/free?fl=straatnaam,woonplaatsnaam,huisnummer,postcode&rows=1&fq=type:adres&q=${q}`;
        const res = await fetch(url, { signal: ctrl.signal });
        if (!res.ok) throw new Error("lookup failed");
        const json = (await res.json()) as {
          response?: { docs?: Array<{ straatnaam?: string; woonplaatsnaam?: string }> };
        };
        const doc = json.response?.docs?.[0];
        if (!doc?.straatnaam || !doc?.woonplaatsnaam) {
          setLookupState("notfound");
          return;
        }
        setEditing((prev) =>
          prev
            ? {
                ...prev,
                street: prev.street && prev.street !== doc.straatnaam ? prev.street : doc.straatnaam!,
                city: prev.city && prev.city !== doc.woonplaatsnaam ? prev.city : doc.woonplaatsnaam!,
                postal_code: `${pc.slice(0, 4)} ${pc.slice(4)}`,
              }
            : prev,
        );
        setLookupState("found");
      } catch (err) {
        if ((err as Error).name !== "AbortError") setLookupState("notfound");
      }
    }, 350);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.postal_code, editing?.house_number]);


  const { data, isLoading } = useQuery({
    queryKey: ["my-addresses"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("addresses")
        .select("*")
        .order("created_at", { ascending: false })
        .returns<Address[]>();
      if (error) throw error;
      return data ?? [];
    },
  });

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    // Adresvalidatie: postcode + huisnummer moeten een geldig NL-adres opleveren
    if (editing.country.trim().toLowerCase() === "nederland") {
      if (lookupState === "loading") {
        alert("Even wachten — adres wordt nog gecontroleerd.");
        return;
      }
      if (lookupState !== "found") {
        alert(
          "Postcode en huisnummer komen niet overeen met een bestaand adres. Controleer de combinatie voordat u opslaat.",
        );
        return;
      }
    }
    setSaving(true);
    const payload = { ...editing, user_id: user!.id };
    const { error } = editing.id
      ? await supabase.from("addresses").update(payload).eq("id", editing.id)
      : await supabase.from("addresses").insert(payload);
    setSaving(false);
    if (error) {
      alert(error.message);
      return;
    }
    setEditing(null);
    qc.invalidateQueries({ queryKey: ["my-addresses"] });
  };

  const del = async (id: string) => {
    if (!confirm("Adres verwijderen?")) return;
    const { error } = await supabase.from("addresses").delete().eq("id", id);
    if (error) {
      alert(error.message);
      return;
    }
    qc.invalidateQueries({ queryKey: ["my-addresses"] });
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageHeader
          icon={MapPin}
          title="Adressen"
          subtitle="Beheer uw bezorg- en factuuradressen."
        />
        <Button onClick={() => setEditing({ ...empty })} className="gap-2">
          <Plus className="size-4" /> Adres toevoegen
        </Button>
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-40 animate-pulse rounded-2xl bg-muted" />
          ))}
        </div>
      ) : !data || data.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card p-12 text-center">
          <MapPin className="size-10 text-text-secondary/60" />
          <p className="mt-3 text-sm font-semibold">Nog geen adressen</p>
          <p className="mt-1 text-xs text-text-secondary">
            Voeg een adres toe om sneller te bestellen.
          </p>
          <Button onClick={() => setEditing({ ...empty })} className="mt-5 gap-2">
            <Plus className="size-4" /> Eerste adres toevoegen
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {data.map((a) => (
            <div
              key={a.id}
              className="group relative overflow-hidden rounded-2xl border border-border bg-card p-5 shadow-sm transition hover:shadow-md"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex size-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#0b2a4a] to-[#3a7bd5] text-white">
                    <MapPin className="size-4" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{a.label || "Adres"}</p>
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      {a.is_shipping && (
                        <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">
                          Bezorging
                        </span>
                      )}
                      {a.is_billing && (
                        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                          Facturatie
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1 opacity-0 transition group-hover:opacity-100">
                  <button
                    onClick={() =>
                      setEditing({
                        id: a.id,
                        label: a.label ?? "",
                        street: a.street,
                        house_number: a.house_number,
                        postal_code: a.postal_code,
                        city: a.city,
                        country: a.country,
                        is_billing: a.is_billing,
                        is_shipping: a.is_shipping,
                      })
                    }
                    className="rounded-lg p-1.5 text-text-secondary hover:bg-muted hover:text-foreground"
                    aria-label="Bewerken"
                  >
                    <Pencil className="size-4" />
                  </button>
                  <button
                    onClick={() => del(a.id)}
                    className="rounded-lg p-1.5 text-text-secondary hover:bg-red-50 hover:text-red-600"
                    aria-label="Verwijderen"
                  >
                    <Trash2 className="size-4" />
                  </button>
                </div>
              </div>
              <div className="mt-4 space-y-0.5 text-sm text-text-secondary">
                <p className="text-foreground">
                  {a.street} {a.house_number}
                </p>
                <p>
                  {a.postal_code} {a.city}
                </p>
                <p>{a.country}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setEditing(null)}
          />
          <form
            onSubmit={save}
            className="relative w-full max-w-lg rounded-2xl bg-card p-6 shadow-2xl"
          >
            <div className="mb-5 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">
                {editing.id ? "Adres bewerken" : "Nieuw adres"}
              </h2>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="rounded-lg p-1.5 text-text-secondary hover:bg-muted"
              >
                <X className="size-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Label (optioneel)" className="col-span-2">
                <input
                  value={editing.label}
                  onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                  className={inputCls}
                  placeholder="bv. Kantoor, Magazijn"
                />
              </FormField>
              <FormField label="Postcode" className="col-span-2 sm:col-span-1">
                <input
                  required
                  value={editing.postal_code}
                  onChange={(e) =>
                    setEditing({ ...editing, postal_code: e.target.value })
                  }
                  className={inputCls}
                  placeholder="1234 AB"
                  autoComplete="postal-code"
                />
              </FormField>
              <FormField label="Huisnummer" className="col-span-2 sm:col-span-1">
                <input
                  required
                  value={editing.house_number}
                  onChange={(e) =>
                    setEditing({ ...editing, house_number: e.target.value })
                  }
                  className={inputCls}
                  autoComplete="address-line2"
                />
              </FormField>
              <div className="col-span-2 -mt-2 text-xs">
                {lookupState === "loading" && (
                  <span className="inline-flex items-center gap-1.5 text-text-secondary">
                    <Loader2 className="size-3 animate-spin" /> Adres opzoeken…
                  </span>
                )}
                {lookupState === "found" && (
                  <span className="text-emerald-600">
                    ✓ Straat en plaats automatisch ingevuld — pas aan indien nodig.
                  </span>
                )}
                {lookupState === "notfound" && (
                  <span className="text-amber-600">
                    Geen adres gevonden voor deze postcode + huisnummer. Vul straat en plaats handmatig in.
                  </span>
                )}
              </div>
              <FormField label="Straat" className="col-span-2 sm:col-span-1">
                <input
                  required
                  value={editing.street}
                  onChange={(e) => setEditing({ ...editing, street: e.target.value })}
                  className={inputCls}
                  autoComplete="address-line1"
                />
              </FormField>
              <FormField label="Plaats" className="col-span-2 sm:col-span-1">
                <input
                  required
                  value={editing.city}
                  onChange={(e) => setEditing({ ...editing, city: e.target.value })}
                  className={inputCls}
                  autoComplete="address-level2"
                />
              </FormField>

              <FormField label="Land" className="col-span-2">
                <input
                  required
                  value={editing.country}
                  onChange={(e) => setEditing({ ...editing, country: e.target.value })}
                  className={inputCls}
                />
              </FormField>
              <div className="col-span-2 flex gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={editing.is_shipping}
                    onChange={(e) =>
                      setEditing({ ...editing, is_shipping: e.target.checked })
                    }
                    className="rounded"
                  />
                  Bezorgadres
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={editing.is_billing}
                    onChange={(e) =>
                      setEditing({ ...editing, is_billing: e.target.checked })
                    }
                    className="rounded"
                  />
                  Factuuradres
                </label>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2 border-t border-border pt-5">
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                Annuleren
              </Button>
              <Button
                type="submit"
                disabled={
                  saving ||
                  (editing.country.trim().toLowerCase() === "nederland" &&
                    lookupState !== "found")
                }
                className="gap-2"
              >
                {saving && <Loader2 className="size-4 animate-spin" />}
                Opslaan
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const inputCls =
  "w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm focus:border-[#3a7bd5] focus:outline-none focus:ring-2 focus:ring-[#3a7bd5]/20";

function FormField({
  label,
  children,
  className = "",
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-text-secondary">
        {label}
      </span>
      {children}
    </label>
  );
}
